import {
  Alert,
  Button,
  Group,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import type React from "react";
import { useEffect, useState } from "react";
import { client } from "@/api-client";
import type { FileOutput } from "@/api-types";
import { Header } from "@/components/Header";
import { TrainerRoutes, useNavigator } from "@/routers/routes";

interface TextNoteEditorProps {
  file?: FileOutput;
  isCreating?: boolean;
  folderIdParam: string | null | undefined;
}

export const TextNoteEditor = ({
  file,
  isCreating = false,
  folderIdParam,
}: TextNoteEditorProps) => {
  const { navigate } = useNavigator();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  const parentFolderId = file?.parentFolderId || folderIdParam || null;

  // For text notes, we need to use a different endpoint
  const createTextNoteMutation = client.useMutation(
    "post",
    "/universal/files/text-note",
  );
  const updateTextNoteMutation = client.useMutation(
    "post",
    "/universal/files/text-note/{noteId}",
  );

  // Load file data if editing
  useEffect(() => {
    if (file) {
      setName(file.name);
      setDescription(file.description || "");

      if (file.content) {
        setContent(
          typeof file.content === "string"
            ? file.content
            : JSON.stringify(file.content),
        );
      }
    }
  }, [file]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Note name is required");
      return;
    }

    if (!content.trim()) {
      setError("Note content is required");
      return;
    }

    try {
      if (isCreating) {
        await createTextNoteMutation.mutateAsync({
          body: {
            name,
            description,
            content,
            parentFolderId,
          },
        });
      } else if (file) {
        await updateTextNoteMutation.mutateAsync({
          params: { path: { noteId: file.id } },
          body: {
            name,
            description,
            content,
            parentFolderId,
          },
        });
      }

      if (folderIdParam && folderIdParam !== "root") {
        navigate({
          path: TrainerRoutes.LIBRARY.FOLDER,
          params: { folderId: folderIdParam },
        });
      } else {
        navigate({ path: TrainerRoutes.LIBRARY.LIST });
      }
    } catch (error) {
      console.error("Error saving text note:", error);
      setError("Failed to save text note. Please try again.");
    }
  };

  return (
    <Stack>
      <Header title={isCreating ? "Create Text Note" : "Edit Text Note"} />

      <form onSubmit={onSubmit}>
        <Stack>
          {error && (
            <Alert color="red" title="Error" icon={<IconAlertCircle />}>
              {error}
            </Alert>
          )}

          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Enter note name"
          />

          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autosize
            minRows={3}
            placeholder="Enter description (optional)"
          />

          <Textarea
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            autosize
            minRows={10}
            required
            placeholder="Enter note content"
          />

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                if (folderIdParam && folderIdParam !== "root") {
                  navigate({
                    path: TrainerRoutes.LIBRARY.FOLDER,
                    params: { folderId: folderIdParam },
                  });
                } else {
                  navigate({
                    path: TrainerRoutes.LIBRARY.LIST,
                  });
                }
              }}
              disabled={
                createTextNoteMutation.isPending ||
                updateTextNoteMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={
                isCreating
                  ? createTextNoteMutation.isPending
                  : updateTextNoteMutation.isPending
              }
            >
              {isCreating ? "Create" : "Save Changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
};
